import { forwardRef, type ComponentRef } from "react";

import { useWindowDimensions } from "@luce/core";

import { cn, platformProps } from "../../lib";
import { markPendingAware } from "../../lib/pending";
import { Box, Pressable } from "../../primitives";
import { buttonIconColor, buttonSpinnerColorFor, buttonSpinnerSize } from "../button";
import { iconButtonIconSize, iconButtonIconStateClass } from "../icon-button/lib";
import { Icon } from "../icons";
import { Spinner } from "../spinner";
import { Typography } from "../typography";
import {
  actionButtonLabelColor,
  actionButtonLabelStateClass,
  actionButtonLabelVariant,
  actionButtonPillClass,
  actionButtonPillFocusClass,
  actionButtonPillSizeClass,
} from "./lib";
import type { ActionButtonProps } from "./types";

/**
 * Icon pill stacked above a caption, as one control: the whole tile is the
 * Pressable (one tap target, one accessible name), and the pill and caption
 * follow its hover and pressed states. The tile's padding keeps every size at
 * 44px or more without hitSlop (Figma: 52×56, 64×66, 72×76).
 */
export const ActionButton = forwardRef<ComponentRef<typeof Pressable>, ActionButtonProps>(
  (
    {
      icon,
      label,
      iconWeight,
      variant = "secondary",
      size = "lg",
      loading = false,
      disabled = false,
      pending = false,
      className,
      testID,
      accessibilityLabel,
      accessibilityState,
      ...pressableProps
    },
    ref,
  ) => {
    const state = disabled ? "disabled" : "default";
    const { isDesktop } = useWindowDimensions();

    return (
      <Pressable
        ref={ref}
        {...pressableProps}
        {...platformProps({ testID, slot: "action-button" })}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityState={{ ...accessibilityState, disabled, busy: loading }}
        disabled={disabled}
        // Loading ignores every activation, not only onClick
        pending={pending || loading}
        // `min-w-0 max-w-full`: a tile its parent sizes (e.g. `flex-1`) can be
        // narrower than its caption, which then ends in "…"
        className={cn(
          "native:flex p-spacing-sm md:p-spacing-md gap-spacing-xs md:gap-spacing-sm group inline-flex min-w-0 max-w-full flex-col items-center justify-center outline-none",
          loading && "cursor-wait",
          className,
        )}
      >
        {/* A span: a button may hold only phrasing content on web */}
        <Box
          as="span"
          className={cn(
            "native:flex native:flex-row rounded-radius-full box-border inline-flex items-center justify-center overflow-hidden",
            actionButtonPillSizeClass[size],
            actionButtonPillClass[variant][state],
            actionButtonPillFocusClass,
          )}
        >
          {loading ? (
            <Spinner
              size={buttonSpinnerSize[size]}
              color={buttonSpinnerColorFor(variant, disabled)}
              decorative
              className="shrink-0"
            />
          ) : (
            <Icon
              name={icon}
              size={iconButtonIconSize[size][isDesktop ? "desktop" : "mobile"]}
              weight={iconWeight}
              className={cn(
                "shrink-0",
                buttonIconColor[variant][state],
                // Figma's pill takes Icon-Button's icon reds (hover and pressed)
                !disabled && iconButtonIconStateClass[variant],
              )}
            />
          )}
        </Box>
        <Typography
          variant={actionButtonLabelVariant[size]}
          color={actionButtonLabelColor[variant][state]}
          align="center"
          whiteSpace="nowrap"
          className={cn("max-w-full truncate", !disabled && actionButtonLabelStateClass[variant])}
        >
          {label}
        </Typography>
      </Pressable>
    );
  },
);

ActionButton.displayName = "ActionButton";
markPendingAware(ActionButton);
